import { Analytics } from '@analytics';
import InfoIcon from '@assets/images/onboarding/Info.svg';
import SplitIcon from '@assets/images/Split.svg';
import TeamTipSplitDetails from '@components/TapToTip/Groups/TeamTipSplitDetails';
import { CardViewType } from '@constants';
import { Button, CircularProgress } from '@material-ui/core';
import { getSortedGuidesForTeam } from '@utils/helpers';
import { useInjectReducer } from '@utils/injectReducer';
import { useInjectSaga } from '@utils/injectSaga';
import React, { useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { connect } from 'react-redux';
import * as actions from './actions';
import messages from './messages';
import reducer from './reducer';
import saga from './saga';

// Tailwind class strings for the MUI buttons (important modifier used to win over MUI defaults)
const primaryButtonClasses =
  'w-full max-w-[354px] h-[39px] !bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !rounded-xl !shadow-sm !normal-case disabled:!bg-slate-300 disabled:!text-white';
const cancelButtonClasses =
  'w-full max-w-[354px] h-[39px] !bg-white hover:!bg-slate-50 !text-slate-500 !font-semibold !rounded-xl !border !border-solid !border-slate-300 !normal-case';
const splitEvenButtonClasses =
  'float-right !max-w-[45px] !min-w-[45px] h-[30px] !text-xs !bg-indigo-600 hover:!bg-indigo-700 !text-white !font-semibold !rounded-xl !shadow-sm !normal-case';

const MAX_SPLIT_PERCENTAGE = 100;

const TeamSettings = ({
  groupId,
  setCardView,
  isLoading,
  teamDetails,
  usersInfo,
  fetchTeamDetails,
  saveSplitBreakdown,
  isSplitBreakdownSaved,
  isSplitBreakdownSaving,
  currentUser,
  clearTeamDetails
}) => {
  useInjectSaga({ key: 'teamSettings', saga });
  useInjectReducer({ key: 'teamSettings', reducer });

  const intl = useIntl();
  const [tipBreakdown, setTipBreakdown] = React.useState([]);

  const totalBreakdown = useMemo(() => {
    return tipBreakdown.reduce((acc, curr) => acc + curr.splitPercentage, 0);
  }, [tipBreakdown]);

  const isSplitPercentageEqual = useMemo(() => {
    const firstTipPercentage = tipBreakdown[0]?.splitPercentage;
    return tipBreakdown.every(user => user.splitPercentage === firstTipPercentage);
  }, [tipBreakdown]);

  const filteredUsers = useMemo(() => {
    if (!teamDetails || !usersInfo) {
      return [];
    }

    return usersInfo.filter(user => teamDetails?.users.includes(user._id));
  }, [teamDetails, usersInfo]);

  const sortedUsers = useMemo(() => {
    if (!filteredUsers.length) return [];

    const currentUserId = currentUser.userId;

    return getSortedGuidesForTeam(filteredUsers, currentUserId);
  }, [filteredUsers, currentUser]);

  useEffect(() => {
    if (filteredUsers.length) {
      // fill tipBreakdown with values from teamDetails.userSplitTip
      // and set the split percentage to 0 if it's not set
      const rawBreakdown = filteredUsers.map(user => ({
        userId: user._id,
        splitPercentage:
          teamDetails.userTipSplit.tipBreakdown.find(userTip => userTip.userId === user._id)
            ?.splitPercentage ?? 0
      }));

      // Calculate total to check for data integrity issues
      const total = rawBreakdown.reduce((acc, curr) => acc + curr.splitPercentage, 0);
      
      // If total is not 100% (due to backend issues, cached data, duplicate operations, etc.)
      // normalize the percentages or reset to even split
      if (Math.abs(total - 100) > 0.01) {
        console.warn(`Split breakdown total is ${total}% for ${filteredUsers.length} users, normalizing to 100%`);
        console.warn('Raw breakdown:', rawBreakdown);
        
        if (total > 0) {
          // Normalize existing percentages to total 100%
          const normalizedBreakdown = rawBreakdown.map(user => ({
            ...user,
            splitPercentage: Math.round((user.splitPercentage / total) * 100 * 100) / 100 // Round to 2 decimal places
          }));
          
          // Ensure the total is exactly 100% after rounding
          const normalizedTotal = normalizedBreakdown.reduce((acc, curr) => acc + curr.splitPercentage, 0);
          if (Math.abs(normalizedTotal - 100) > 0.01) {
            // Adjust the first user's percentage to make the total exactly 100%
            const adjustment = 100 - normalizedTotal;
            normalizedBreakdown[0].splitPercentage = Math.round((normalizedBreakdown[0].splitPercentage + adjustment) * 100) / 100;
          }
          
          setTipBreakdown(normalizedBreakdown);
        } else {
          // If all percentages are 0, set even split with proper remainder distribution
          const baseAmount = Math.floor((100 / filteredUsers.length) * 100) / 100;
          const remainder = Math.round((100 - (baseAmount * filteredUsers.length)) * 100) / 100;
          
          const normalizedBreakdown = rawBreakdown.map((user, index) => ({
            ...user,
            splitPercentage: index === 0 ? baseAmount + remainder : baseAmount
          }));
          setTipBreakdown(normalizedBreakdown);
        }
      } else {
        setTipBreakdown(rawBreakdown);
      }
    }
  }, [teamDetails, filteredUsers]);

  useEffect(() => {
    if (!teamDetails && !isLoading) {
      fetchTeamDetails(groupId);
    }
  }, [groupId]);

  useEffect(() => {
    if (isSplitBreakdownSaved) {
      setCardView(CardViewType.TeamDetails);
    }
  }, [isSplitBreakdownSaved]);

  useEffect(() => {
    Analytics.trackViewFeature('TeamSplitSettings');
  }, []);

  const handleSplitPercentageChange = useCallback(({ userId, splitPercentage }) => {
    setTipBreakdown(prev =>
      prev.map(user => (user.userId === userId ? { ...user, splitPercentage } : user))
    );
  }, []);

  const handleEvenSplitClicked = () => {
    Analytics.track('Split Tips Evenly Clicked');
    
    // Fix for floating-point precision and ensure total equals exactly 100%
    const baseAmount = Math.floor((100 / filteredUsers.length) * 100) / 100;
    const remainder = Math.round((100 - (baseAmount * filteredUsers.length)) * 100) / 100;
    
    setTipBreakdown(prev =>
      prev.map((user, index) => {
        // Distribute remainder to first few users
        const splitPercentage = index === 0 ? baseAmount + remainder : baseAmount;
        return { ...user, splitPercentage };
      })
    );
  };

  const handleSaveSplitBreakdown = useCallback(() => {
    Analytics.track('SplitTipSaved', { groupId });
    Analytics.trackUseFeature('TeamSplitSettings');

    saveSplitBreakdown({
      groupId,
      tipBreakdown: tipBreakdown.map(user => ({
        userId: user.userId,
        splitPercentage: user.splitPercentage
      }))
    });
  });

  const handleCancel = useCallback(() => {
    // clear up unsaved changes
    setTipBreakdown([]);

    // clear team details before showing the tipping team, to fetch the latest data
    clearTeamDetails();
    setCardView(CardViewType.TeamDetails);
  });

  if (isLoading) {
    return (
      <div className="flex flex-col gap-8 justify-center items-center h-full w-full">
        <CircularProgress />
      </div>
    );
  }

  if (!teamDetails) return null;

  return (
    <div className="flex flex-col gap-8">
      <div className="flex w-full h-auto p-4 rounded-3xl shadow-sm border border-solid border-slate-200 bg-slate-50 gap-2 items-start">
        <img src={InfoIcon} className="w-6 h-6" alt="Split Tip" />
        <span className="text-xs font-semibold text-slate-500">
          {intl.formatMessage(
            isSplitPercentageEqual ? messages.infoBoxTextEqual : messages.infoBoxTextNotEqual
          )}
        </span>
      </div>

      <div className="px-5 py-4 shadow-sm border border-solid border-slate-200 rounded-3xl bg-slate-50 flex flex-col justify-start flex-[1_0_0%] m-auto w-full">
        <div className="flex flex-row mb-5">
          <div className="flex w-10 h-10 justify-center items-center">
            <img src={SplitIcon} alt="Split Tip" />
          </div>
          <div className="flex flex-col pl-2 flex-1">
            <div className="text-base font-bold text-slate-800">{intl.formatMessage(messages.splitYourTips)}</div>
            <div className="text-xs font-semibold text-slate-400">
              {intl.formatMessage(messages.setYourSplitPercentage)}
            </div>
          </div>
          <Button
            className={splitEvenButtonClasses}
            onClick={handleEvenSplitClicked}
            title="Split tips evenly">
            50/50
          </Button>
        </div>

        {sortedUsers.map(user => (
          <TeamTipSplitDetails
            key={user._id}
            avatarUrl={user.profile.avatarUrl}
            firstName={user.profile.firstName}
            lastName={user.profile.lastName}
            isTheSameUser={currentUser.userId === user._id}
            tipBreakdown={tipBreakdown}
            userId={user._id}
            onSplitPercentageChange={handleSplitPercentageChange}
          />
        ))}
      </div>
      <div className="flex gap-4 items-center flex-col justify-center">
        <Button
          className={primaryButtonClasses}
          disabled={isSplitBreakdownSaving || totalBreakdown !== MAX_SPLIT_PERCENTAGE}
          onClick={handleSaveSplitBreakdown}>
          {intl.formatMessage(messages.save)}
        </Button>
        <Button className={cancelButtonClasses} onClick={handleCancel}>
          {intl.formatMessage(messages.cancel)}
        </Button>
      </div>
    </div>
  );
};

const mapStateToProps = state => ({
  isLoading: state.teamSettings?.isLoading,
  teamDetails: state.teamSettings?.teamDetails,
  isSplitBreakdownSaved: state.teamSettings?.isSplitBreakdownSaved,
  isSplitBreakdownSaving: state.teamSettings?.isSplitBreakdownSaving,
  user: state.auth?.user
});

const mapDispatchToProps = dispatch => ({
  fetchTeamDetails: groupId => dispatch(actions.fetchTeamDetails(groupId)),
  saveSplitBreakdown: splitBreakdown => dispatch(actions.saveSplitBreakdown(splitBreakdown)),
  clearTeamDetails: () => dispatch(actions.clearTeamDetails())
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(TeamSettings);
